import { type ChildProcessWithoutNullStreams, spawn } from 'node:child_process';
import { randomUUID } from 'node:crypto';
import {
  closeSync,
  createReadStream,
  lstatSync,
  openSync,
  realpathSync,
  writeSync,
} from 'node:fs';
import {
  mkdir,
  mkdtemp,
  readdir,
  readFile,
  rm,
  stat,
  unlink,
  writeFile,
} from 'node:fs/promises';
import { tmpdir, userInfo } from 'node:os';
import {
  dirname,
  isAbsolute,
  join,
  posix as pathPosix,
  relative,
  resolve,
} from 'node:path';
import { UserError } from '../../errors';
import type { ToolOutputImage } from '../../types/protocol';
import {
  normalizeSandboxClientCreateArgs,
  type SandboxClient,
  type SandboxClientCreateArgs,
  type SandboxClientOptions,
  type SandboxConcurrencyLimits,
} from '../client';
import { SandboxConfigurationError } from '../errors';
import { type Manifest, normalizeRelativePath } from '../manifest';
import type {
  EditFileArgs,
  EditFileResult,
  ExecCommandArgs,
  ExecForegroundArgs,
  ExecForegroundArgvArgs,
  ExposedPortEndpoint,
  GlobFilesArgs,
  GlobFilesResult,
  GrepFilesArgs,
  GrepFilesResult,
  ListDirectoryArgs,
  MaterializeEntryArgs,
  ReadFileArgs,
  ReadTextFileArgs,
  ReadTextFileResult,
  SandboxDirectoryEntry,
  SandboxExecResult,
  SandboxSession,
  SandboxSessionState,
  StatFileArgs,
  StatFileResult,
  ViewImageArgs,
  WriteFileArgs,
  WriteFileResult,
  WriteStdinArgs,
} from '../session';
import { normalizeExposedPort, recordExposedPortEndpoint } from '../session';
import {
  isHostPathWithinRoot,
  relativeHostPathEscapesRoot,
} from '../shared/host-path';
import {
  assertViewImageByteLength,
  imageOutputFromBytes,
} from '../shared/media';
import {
  elapsedSeconds,
  formatExecResponse,
  truncateOutput,
} from '../shared/output';
import {
  type ResolvedSandboxPath,
  type ResolveSandboxPathOptions,
  WorkspacePathPolicy,
} from '../workspace-paths';
import {
  buildRipgrepGlobArgs,
  createGlobFilesResult,
  globSearchPathCandidate,
  resolveGlobSearch,
} from './shared/glob';
import {
  buildRipgrepArgs,
  createGrepFilesResult,
  grepOutputMode,
  ripgrepOutputLines,
} from './shared/grep';
import {
  canReuseLocalSnapshotWorkspace,
  createWorkspaceArchive,
  localSnapshotIsRestorable,
  persistLocalSnapshot,
  restoreLocalSnapshotToWorkspace,
  restoreWorkspaceArchive,
  serializeLocalSnapshotSpec,
} from './shared/local-snapshots';
import {
  applyOwnershipRecursive,
  assertLocalWorkspaceManifestMetadataSupported,
  joinSandboxLogicalPath,
  materializeLocalWorkspaceManifest,
  materializeLocalWorkspaceManifestEntry,
  materializeLocalWorkspaceManifestMounts,
  pathExists,
} from './shared/local-workspace';
import {
  mergeManifestDelta,
  mergeManifestEntryDelta,
  sanitizeEnvironmentForPersistence,
  serializeManifest,
} from './shared/manifest-persistence';
import { spawnInPseudoTerminal } from './shared/pty';
import {
  formatSandboxProcessError,
  runSandboxProcess,
} from './shared/run-process';
import {
  deserializeLocalSandboxSessionStateValues,
  normalizeExposedPorts,
} from './shared/session-state-values';
import { resolveLocalShellCommand } from './shared/shell-command';
import type { LocalSandboxSnapshot, LocalSandboxSnapshotSpec } from './types';

const DEFAULT_EXEC_YIELD_TIME_MS = 10_000;
const DEFAULT_WRITE_STDIN_YIELD_TIME_MS = 250;
const DEFAULT_EXEC_FOREGROUND_TIMEOUT_MS = 120_000;
const DEFAULT_EXEC_FOREGROUND_OUTPUT_PREVIEW_BYTES = 30_000;
const MAX_EXEC_FOREGROUND_OUTPUT_PREVIEW_BYTES = 150_000;
const MAX_FOREGROUND_OUTPUT_FILE_BYTES = 64 * 1024 * 1024;
const TOOL_RESULTS_DIR = '.agent/tool-results';
const RUN_AS_LOOKUP_TIMEOUT_MS = 10_000;
const MAX_ACTIVE_PROCESS_OUTPUT_CHARS = 1024 * 1024;
const GREP_TIMEOUT_MS = 20_000;
const GREP_MAX_OUTPUT_BYTES = 20_000_000;
const DEFAULT_SANDBOX_COMMAND_PATH =
  '/opt/homebrew/bin:/usr/local/bin:/usr/bin:/bin:/usr/sbin:/sbin';

type ActiveProcessOutputStream = 'stdout' | 'stderr';

type ForegroundOutputCapture = {
  id: string;
  outputFilePath: string;
  outputMetadataPath: string;
  logicalOutputFilePath: string;
  logicalOutputMetadataPath: string;
  previewBytes: number;
  fd: number;
  translator: StreamingOutputTranslator;
  preview: string;
  previewByteLength: number;
  bytesSeen: number;
  bytesWritten: number;
  fileTruncated: boolean;
  writeError?: string;
  closed: boolean;
};

type ActiveProcess = {
  child: ChildProcessWithoutNullStreams;
  tty: boolean;
  processGroup: boolean;
  outputCapture?: ForegroundOutputCapture;
  output: string;
  stdout: string;
  stderr: string;
  droppedOutputChars: number;
  droppedStdoutChars: number;
  droppedStderrChars: number;
  exitCode: number | null;
  done: boolean;
  donePromise: Promise<void>;
  resolveDone: () => void;
  outputClosedPromise: Promise<void>;
  resolveOutputClosed: () => void;
};

type UnixRunAsIdentity = {
  username: string;
  uid: number;
  gid: number;
  isCurrentUser: boolean;
};

export interface UnixLocalSandboxClientOptions extends SandboxClientOptions {
  /**
   * 新建沙箱工作目录的父目录。每次 create() 会在这里创建一个
   * 临时目录；未传时使用系统临时目录。
   */
  workspaceBaseDir?: string;

  /**
   * 会话序列化和恢复时使用的快照策略。local 会把工作区快照写到本机目录，
   * remote 会通过配置的 store 保存/读取快照，noop 表示不持久化快照。
   */
  snapshot?: LocalSandboxSnapshotSpec;

  /**
   * 命令未显式传 shell 时使用的默认 shell。单次 exec/execForeground
   * 传入的 shell 优先级更高；这里也未设置时会按环境变量和系统默认 shell 回退。
   */
  defaultShell?: string;

  /**
   * 允许通过 resolveExposedPort() 暴露的端口白名单。端口会被校验并去重；
   * 未传或为空数组时不限制可解析的端口。
   */
  exposedPorts?: number[];

  /**
   * 物化 manifest 时的并发限制。manifestEntries 控制顶层 manifest entry
   * 的并发数，localDirFiles 控制复制 local_dir 内容时的文件并发数。
   */
  concurrencyLimits?: SandboxConcurrencyLimits;
}

export interface UnixLocalSandboxSessionState extends SandboxSessionState {
  workspaceRootPath: string;
  workspaceRootOwned: boolean;
  environment: Record<string, string>;
  snapshotSpec?: LocalSandboxSnapshotSpec | null;
  snapshot?: LocalSandboxSnapshot | null;
  snapshotFingerprint?: string | null;
  snapshotFingerprintVersion?: string | null;
  configuredExposedPorts?: number[];
}

export class UnixLocalSandboxSession<
  TState extends UnixLocalSandboxSessionState = UnixLocalSandboxSessionState,
> implements SandboxSession<TState>
{
  readonly state: TState;
  protected readonly defaultShell?: string;
  private readonly activeProcesses = new Map<number, ActiveProcess>();
  private nextSessionId = 1;
  private closed = false;

  constructor(args: { state: TState; defaultShell?: string }) {
    this.state = args.state;
    this.defaultShell = args.defaultShell;
  }

  supportsPty(): boolean {
    return true;
  }

  async resolveExposedPort(port: number): Promise<ExposedPortEndpoint> {
    const exposedPort = normalizeExposedPort(port);
    const configuredPorts = this.state.configuredExposedPorts ?? [];
    if (configuredPorts.length > 0 && !configuredPorts.includes(exposedPort)) {
      throw new SandboxConfigurationError(
        `UnixLocalSandboxClient was not configured to expose port ${exposedPort}.`,
        {
          provider: 'UnixLocalSandboxClient',
          port: exposedPort,
          configuredPorts,
        },
      );
    }
    return recordExposedPortEndpoint(this.state, {
      host: '127.0.0.1',
      port: exposedPort,
      tls: false,
    });
  }

  async execCommand(args: ExecCommandArgs): Promise<string> {
    return formatExecResponse(await this.exec(args));
  }

  async execForeground(args: ExecForegroundArgs): Promise<SandboxExecResult> {
    const cwd = this.resolveCommandWorkdir(args.workdir);
    const logicalCwd = this.logicalWorkdirForPath(args.workdir);
    return this.runForegroundProcess({
      displayCommand: args.cmd,
      outputPreviewBytes: args.outputPreviewBytes,
      outputId: args.outputId,
      timeoutMs: args.timeoutMs,
      maxOutputTokens: args.maxOutputTokens,
      spawnChild: () =>
        this.spawnShellCommand(this.translateCommandInput(args.cmd), {
          cwd,
          logicalCwd,
          shell: args.shell,
          login: args.login ?? true,
          runAs: args.runAs,
          tty: false,
          processGroup: true,
        }),
    });
  }

  /**
   * 以 argv 形式直接 spawn 单个二进制,**不经过 shell**。每个声明为路径的参数
   * 先经 resolveSandboxPath 解析+约束在工作区内(逃逸即抛错),再把解析后的宿主
   * 绝对路径替换回 argv。其余 token(命令名、白名单 flag、`--`)原样传递。
   */
  async execForegroundArgv(
    args: ExecForegroundArgvArgs,
  ): Promise<SandboxExecResult> {
    const cwd = this.resolveCommandWorkdir(args.workdir);
    const logicalCwd = this.logicalWorkdirForPath(args.workdir);
    const resolvedArgs = [...args.args];
    for (const pathArg of args.pathArgs ?? []) {
      resolvedArgs[pathArg.index] = this.resolveSandboxPath(
        resolvedArgs[pathArg.index],
        { forWrite: pathArg.forWrite },
      );
    }
    return this.runForegroundProcess({
      displayCommand: [args.command, ...args.args].join(' '),
      outputPreviewBytes: args.outputPreviewBytes,
      outputId: args.outputId,
      timeoutMs: args.timeoutMs,
      maxOutputTokens: args.maxOutputTokens,
      spawnChild: () =>
        this.spawnDirectCommand(args.command, resolvedArgs, {
          cwd,
          logicalCwd,
          runAs: args.runAs,
          processGroup: true,
        }),
    });
  }

  private async runForegroundProcess(params: {
    displayCommand: string;
    outputPreviewBytes?: number;
    outputId?: string;
    timeoutMs?: number;
    maxOutputTokens?: number;
    spawnChild: () => Promise<ChildProcessWithoutNullStreams>;
  }): Promise<SandboxExecResult> {
    const start = Date.now();
    const outputCapture = await this.createForegroundOutputCapture({
      outputPreviewBytes: params.outputPreviewBytes,
      outputId: params.outputId,
    });
    let child: ChildProcessWithoutNullStreams;
    try {
      child = await params.spawnChild();
    } catch (error) {
      closeForegroundOutputCapture(outputCapture);
      await unlink(outputCapture.outputFilePath).catch(() => {});
      throw error;
    }
    const activeProcess = this.trackChildProcess(child, {
      tty: false,
      processGroup: true,
      outputCapture,
    });
    const timeoutMs = params.timeoutMs ?? DEFAULT_EXEC_FOREGROUND_TIMEOUT_MS;

    await waitForProcessOrTimeout(activeProcess, timeoutMs);
    const timedOut = !activeProcess.done;
    if (timedOut) {
      appendActiveProcessOutput(
        activeProcess,
        `Command timed out after ${timeoutMs}ms.\n`,
        'stderr',
      );
      await this.terminateActiveProcess(activeProcess);
    }

    const wallTimeSeconds = elapsedSeconds(start);
    const exitCode = timedOut ? 124 : (activeProcess.exitCode ?? 1);
    const persistedOutput = await finalizeForegroundOutputCapture(
      activeProcess.outputCapture,
      {
        command: params.displayCommand,
        exitCode,
        wallTimeSeconds,
      },
    );
    const processOutput = consumeActiveProcessOutput(activeProcess);
    const output = persistedOutput.persisted
      ? {
          text: buildPersistedOutputMessage(persistedOutput),
          originalTokenCount: approximateTokenCountFromBytes(
            persistedOutput.bytesSeen,
          ),
        }
      : truncateOutput(
          this.translateCommandOutput(processOutput.output),
          params.maxOutputTokens,
        );
    return {
      output: output.text,
      stdout: this.translateCommandOutput(processOutput.stdout),
      stderr: this.translateCommandOutput(processOutput.stderr),
      wallTimeSeconds,
      exitCode,
      originalTokenCount: output.originalTokenCount,
      ...(persistedOutput.persisted
        ? {
            outputFilePath: persistedOutput.logicalOutputFilePath,
            outputMetadataPath: persistedOutput.logicalOutputMetadataPath,
            outputFileSizeBytes: persistedOutput.bytesWritten,
            outputPreviewBytes: persistedOutput.previewBytes,
            outputTruncated: persistedOutput.fileTruncated,
          }
        : {}),
    };
  }

  async exec(args: ExecCommandArgs): Promise<SandboxExecResult> {
    const start = Date.now();
    const cwd = this.resolveCommandWorkdir(args.workdir);
    const logicalCwd = this.logicalWorkdirForPath(args.workdir);
    const child = await this.spawnShellCommand(
      this.translateCommandInput(args.cmd),
      {
        cwd,
        logicalCwd,
        shell: args.shell,
        login: args.login ?? true,
        runAs: args.runAs,
        tty: args.tty ?? false,
      },
    );
    const activeProcess = this.trackChildProcess(child, {
      tty: args.tty ?? false,
    });

    if (!args.tty) {
      await waitForProcessOrTimeout(
        activeProcess,
        args.yieldTimeMs ?? DEFAULT_EXEC_YIELD_TIME_MS,
      );
      const processOutput = consumeActiveProcessOutput(activeProcess);
      if (activeProcess.done) {
        const output = truncateOutput(
          this.translateCommandOutput(processOutput.output),
          args.maxOutputTokens,
        );
        return {
          output: output.text,
          stdout: this.translateCommandOutput(processOutput.stdout),
          stderr: this.translateCommandOutput(processOutput.stderr),
          wallTimeSeconds: elapsedSeconds(start),
          exitCode: activeProcess.exitCode ?? 1,
          originalTokenCount: output.originalTokenCount,
        };
      }

      const output = truncateOutput(
        this.translateCommandOutput(processOutput.output),
        args.maxOutputTokens,
      );
      const sessionId = this.allocateProcessId(activeProcess);
      return {
        output: output.text,
        stdout: this.translateCommandOutput(processOutput.stdout),
        stderr: this.translateCommandOutput(processOutput.stderr),
        wallTimeSeconds: elapsedSeconds(start),
        sessionId,
        originalTokenCount: output.originalTokenCount,
      };
    }

    const yieldTimeMs = args.yieldTimeMs ?? DEFAULT_EXEC_YIELD_TIME_MS;

    await waitForProcessOrTimeout(activeProcess, yieldTimeMs);

    if (activeProcess.done) {
      const processOutput = consumeActiveProcessOutput(activeProcess);
      const output = truncateOutput(
        this.translateCommandOutput(processOutput.output),
        args.maxOutputTokens,
      );
      return {
        output: output.text,
        stdout: this.translateCommandOutput(processOutput.stdout),
        stderr: this.translateCommandOutput(processOutput.stderr),
        wallTimeSeconds: elapsedSeconds(start),
        exitCode: activeProcess.exitCode ?? 1,
        originalTokenCount: output.originalTokenCount,
      };
    }

    const sessionId = this.allocateProcessId(activeProcess);
    const processOutput = consumeActiveProcessOutput(activeProcess);
    const output = truncateOutput(
      this.translateCommandOutput(processOutput.output),
      args.maxOutputTokens,
    );
    return {
      output: output.text,
      stdout: this.translateCommandOutput(processOutput.stdout),
      stderr: this.translateCommandOutput(processOutput.stderr),
      wallTimeSeconds: elapsedSeconds(start),
      sessionId,
      originalTokenCount: output.originalTokenCount,
    };
  }

  async writeStdin(args: WriteStdinArgs): Promise<string> {
    const activeProcess = this.activeProcesses.get(args.sessionId);
    if (!activeProcess) {
      return formatExecResponse({
        output: `write_stdin failed: session not found: ${args.sessionId}`,
        wallTimeSeconds: 0,
        exitCode: 1,
      });
    }

    const start = Date.now();
    const chars = args.chars ?? '';
    if (!activeProcess.tty && chars.includes('\u0003')) {
      activeProcess.child.kill('SIGINT');
    }
    const remainingChars = activeProcess.tty
      ? chars
      : chars.split('\u0003').join('');
    if (remainingChars.length > 0) {
      activeProcess.child.stdin.write(remainingChars);
    }

    await waitForProcessOrTimeout(
      activeProcess,
      args.yieldTimeMs ?? DEFAULT_WRITE_STDIN_YIELD_TIME_MS,
    );

    const processOutput = consumeActiveProcessOutput(activeProcess);
    const output = truncateOutput(
      this.translateCommandOutput(processOutput.output),
      args.maxOutputTokens,
    );
    if (activeProcess.done) {
      this.activeProcesses.delete(args.sessionId);
      return formatExecResponse({
        output: output.text,
        wallTimeSeconds: elapsedSeconds(start),
        exitCode: activeProcess.exitCode ?? 1,
        originalTokenCount: output.originalTokenCount,
      });
    }

    return formatExecResponse({
      output: output.text,
      wallTimeSeconds: elapsedSeconds(start),
      sessionId: args.sessionId,
      originalTokenCount: output.originalTokenCount,
    });
  }

  async viewImage(args: ViewImageArgs): Promise<ToolOutputImage> {
    await this.resolveFilesystemRunAs(args.runAs);
    const filePath = this.resolveSandboxPath(args.path);
    const info = await stat(filePath).catch(() => {
      throw new UserError(`Image file not found: ${args.path}`);
    });
    if (!info.isFile()) {
      throw new UserError(`Image path is not a file: ${args.path}`);
    }
    assertViewImageByteLength(args.path, info.size, args.maxBytes);
    const bytes = await readFile(filePath);
    return imageOutputFromBytes(args.path, bytes, { maxBytes: args.maxBytes });
  }

  async pathExists(path: string, runAs?: string): Promise<boolean> {
    await this.resolveFilesystemRunAs(runAs);
    return await pathExists(this.resolveSandboxPath(path));
  }

  async readFile(args: ReadFileArgs): Promise<Uint8Array> {
    await this.resolveFilesystemRunAs(args.runAs);
    const bytes = await readFile(this.resolveSandboxPath(args.path));
    if (typeof args.maxBytes === 'number' && bytes.byteLength > args.maxBytes) {
      return bytes.subarray(0, args.maxBytes);
    }
    return bytes;
  }

  async statFile(args: StatFileArgs): Promise<StatFileResult> {
    await this.resolveFilesystemRunAs(args.runAs);
    const filePath = this.resolveSandboxPath(args.path);
    const info = await stat(filePath).catch((error) => {
      if ((error as NodeJS.ErrnoException).code === 'ENOENT') {
        return null;
      }
      throw error;
    });
    return {
      path: args.path,
      exists: info !== null,
      isFile: info?.isFile() ?? false,
      size: info?.size ?? 0,
      mtimeMs: info?.mtimeMs ?? 0,
    };
  }

  async readTextFile(args: ReadTextFileArgs): Promise<ReadTextFileResult> {
    await this.resolveFilesystemRunAs(args.runAs);
    const filePath = this.resolveSandboxPath(args.path);
    return await readLocalTextFileRange(filePath, args);
  }

  async grepFiles(args: GrepFilesArgs): Promise<GrepFilesResult> {
    await this.resolveFilesystemRunAs(args.runAs);
    const searchPath = this.resolveSandboxPath(args.path);
    const info = await stat(searchPath).catch((error) => {
      if ((error as NodeJS.ErrnoException).code === 'ENOENT') {
        throw new UserError(`路径不存在：${args.path ?? '.'}`);
      }
      throw error;
    });
    if (!info.isFile() && !info.isDirectory()) {
      throw new UserError(`路径不是文件或目录：${args.path ?? '.'}`);
    }

    const result = await runSandboxProcess(
      'rg',
      buildRipgrepArgs(args, searchPath),
      {
        timeoutMs: GREP_TIMEOUT_MS,
        maxOutputBytes: GREP_MAX_OUTPUT_BYTES,
      },
    );
    if (result.status === 1) {
      return createGrepFilesResult(
        args,
        [],
        this.grepDisplayPathFormatter(args, searchPath, info.isFile()),
      );
    }
    if (result.status !== 0) {
      if (
        result.error &&
        'code' in result.error &&
        result.error.code === 'ENOENT'
      ) {
        throw new UserError('Grep 需要 ripgrep (rg)，但当前环境不可用。');
      }
      throw new UserError(
        `ripgrep 搜索失败：${formatSandboxProcessError(result)}`,
      );
    }

    let lines = ripgrepOutputLines(result.stdout);
    if (grepOutputMode(args.outputMode) === 'files_with_matches') {
      lines = await this.sortGrepFileMatches(lines);
    }
    return createGrepFilesResult(
      args,
      lines,
      this.grepDisplayPathFormatter(args, searchPath, info.isFile()),
    );
  }

  async globFiles(args: GlobFilesArgs): Promise<GlobFilesResult> {
    await this.resolveFilesystemRunAs(args.runAs);
    const startedAt = Date.now();
    const { searchPath, searchPattern } = resolveGlobSearch(args, (path) =>
      this.resolveSandboxPath(path),
    );
    const displaySearchPath = globSearchPathCandidate(args) ?? '.';
    const info = await stat(searchPath).catch((error) => {
      if ((error as NodeJS.ErrnoException).code === 'ENOENT') {
        throw new UserError(`路径不存在：${displaySearchPath}`);
      }
      throw error;
    });
    if (!info.isDirectory()) {
      throw new UserError(`路径不是目录：${displaySearchPath}`);
    }

    const result = await runSandboxProcess(
      'rg',
      buildRipgrepGlobArgs(searchPattern),
      {
        cwd: searchPath,
        timeoutMs: GREP_TIMEOUT_MS,
        maxOutputBytes: GREP_MAX_OUTPUT_BYTES,
      },
    );
    if (result.status === 1) {
      return createGlobFilesResult(
        args,
        [],
        this.globDisplayPathFormatter(args, searchPath),
        startedAt,
      );
    }
    if (result.status !== 0) {
      if (
        result.error &&
        'code' in result.error &&
        result.error.code === 'ENOENT'
      ) {
        throw new UserError('Glob 需要 ripgrep (rg)，但当前环境不可用。');
      }
      throw new UserError(
        `ripgrep 文件匹配失败：${formatSandboxProcessError(result)}`,
      );
    }

    const paths = ripgrepOutputLines(result.stdout).map((path) =>
      isAbsolute(path) ? path : resolve(searchPath, path),
    );
    return createGlobFilesResult(
      args,
      paths,
      this.globDisplayPathFormatter(args, searchPath),
      startedAt,
    );
  }

  async writeFile(args: WriteFileArgs): Promise<WriteFileResult> {
    const identity = await this.resolveFilesystemRunAs(args.runAs);
    const filePath = this.resolveSandboxPath(args.path, {
      forWrite: true,
    });
    const existed = await pathExists(filePath);
    await mkdir(dirname(filePath), { recursive: true });
    await writeFile(filePath, args.content, 'utf8');
    if (identity) {
      await applyOwnershipRecursive(filePath, identity.uid, identity.gid);
    }
    return {
      path: args.path,
      type: existed ? 'update' : 'create',
      bytesWritten: Buffer.byteLength(args.content, 'utf8'),
    };
  }

  async editFile(args: EditFileArgs): Promise<EditFileResult> {
    if (args.oldString === args.newString) {
      throw new UserError(
        '没有可执行的修改：oldString 和 newString 完全相同。',
      );
    }
    if (args.oldString.length === 0) {
      throw new UserError('oldString 不能为空。请改用 Write 完整写入文件。');
    }

    const identity = await this.resolveFilesystemRunAs(args.runAs);
    const filePath = this.resolveSandboxPath(args.path, {
      forWrite: true,
    });
    const current = await readFile(filePath, 'utf8').catch((error) => {
      if ((error as NodeJS.ErrnoException).code === 'ENOENT') {
        throw new UserError(`文件不存在：${args.path}`);
      }
      throw error;
    });
    const matches = countOccurrences(current, args.oldString);
    if (matches === 0) {
      throw new UserError(
        `文件中未找到要替换的字符串。\n字符串：${args.oldString}`,
      );
    }
    if (matches > 1 && !args.replaceAll) {
      throw new UserError(
        `要替换的字符串共匹配 ${matches} 处，但 replaceAll 为 false。若要替换全部匹配，请设置 replaceAll 为 true；若只替换其中一处，请在 oldString 中提供更多上下文。`,
      );
    }

    const next = args.replaceAll
      ? current.split(args.oldString).join(args.newString)
      : current.replace(args.oldString, args.newString);
    await writeFile(filePath, next, 'utf8');
    if (identity) {
      await applyOwnershipRecursive(filePath, identity.uid, identity.gid);
    }
    return {
      path: args.path,
      replacements: args.replaceAll ? matches : 1,
    };
  }

  async listDir(args: ListDirectoryArgs): Promise<SandboxDirectoryEntry[]> {
    await this.resolveFilesystemRunAs(args.runAs);
    const logicalPath = this.resolveLogicalPath(args.path);
    const entries = await readdir(this.resolveSandboxPath(args.path), {
      withFileTypes: true,
    });
    const result: SandboxDirectoryEntry[] = entries.map((entry) => ({
      name: entry.name,
      path: logicalPath ? `${logicalPath}/${entry.name}` : entry.name,
      type: entry.isDirectory() ? 'dir' : entry.isFile() ? 'file' : 'other',
    }));

    // Overlay logical mount targets whose parent is this directory, so the
    // workspace listing reflects its logical layout (e.g. a read-only bind
    // mount at /workspace/documents shows up under `LS /workspace`). Mounts are
    // logical-only here — nothing is materialised on disk — so a host-side
    // `readdir` of the workspace still cannot see them; this only surfaces them
    // through the sandbox's own path resolution, which is the layer that
    // already enforces the mount's read-only / containment rules.
    const seen = new Set(result.map((entry) => entry.name));
    for (const { mountPath } of this.state.manifest.mountTargets()) {
      const rel = this.safeLogicalPath(mountPath);
      if (!rel) continue;
      const slash = rel.lastIndexOf('/');
      const parent = slash === -1 ? '' : rel.slice(0, slash);
      const name = slash === -1 ? rel : rel.slice(slash + 1);
      if (parent !== logicalPath || seen.has(name)) continue;
      seen.add(name);
      result.push({
        name,
        path: logicalPath ? `${logicalPath}/${name}` : name,
        type: 'dir',
      });
    }
    return result;
  }

  async materializeEntry(args: MaterializeEntryArgs): Promise<void> {
    const runAs = await this.resolveFilesystemRunAs(args.runAs);
    const logicalPath = this.resolveLogicalPath(args.path);

    // local_bind 挂载内的路径直接物化到挂载源目录，与 readFile/writeFile 等
    // 文件操作的挂载解析保持一致（否则会在工作区里落一份 shadow 拷贝）。挂载
    // 条目本身已覆盖该子树，不再合并 manifest 增量。
    const mountTarget = this.resolveLocalBindMountTarget(
      logicalPath,
      args.path,
      {
        forWrite: true,
      },
    );
    if (mountTarget) {
      await materializeLocalWorkspaceManifestEntry(
        mountTarget.source,
        mountTarget.childPath,
        args.entry,
      );
      if (runAs) {
        await applyOwnershipRecursive(
          mountTarget.resolvedPath,
          runAs.uid,
          runAs.gid,
        );
      }
      return;
    }

    await materializeLocalWorkspaceManifestEntry(
      this.state.workspaceRootPath,
      logicalPath,
      args.entry,
    );
    if (runAs) {
      await applyOwnershipRecursive(
        this.resolveSandboxPath(args.path),
        runAs.uid,
        runAs.gid,
      );
    }
    this.state.manifest = mergeManifestEntryDelta(
      this.state.manifest,
      logicalPath,
      args.entry,
    );
  }

  async applyManifest(manifest: Manifest, runAs?: string): Promise<void> {
    assertLocalWorkspaceManifestMetadataSupported(
      'UnixLocalSandboxClient',
      manifest,
    );
    const identity = await this.resolveFilesystemRunAs(runAs);
    await materializeLocalWorkspaceManifest(
      manifest,
      this.state.workspaceRootPath,
    );
    const environment = await manifest.resolveEnvironment();
    if (identity) {
      for (const path of Object.keys(manifest.entries)) {
        await applyOwnershipRecursive(
          this.resolveSandboxPath(path),
          identity.uid,
          identity.gid,
        );
      }
    }
    this.state.environment = {
      ...this.state.environment,
      ...environment,
    };
    this.state.manifest = mergeManifestDelta(this.state.manifest, manifest);
  }

  async persistWorkspace(): Promise<Uint8Array> {
    return await createWorkspaceArchive(
      this.state.workspaceRootPath,
      this.state.manifest.ephemeralPersistencePaths(),
    );
  }

  async hydrateWorkspace(
    data: string | ArrayBuffer | Uint8Array,
  ): Promise<void> {
    await restoreWorkspaceArchive(data, this.state.workspaceRootPath);
    await this.materializeRestoredWorkspaceMounts();
  }

  async stop(): Promise<void> {
    await this.stopActiveProcesses();
  }

  async shutdown(): Promise<void> {
    await this.close();
  }

  async delete(): Promise<void> {
    await this.close();
  }

  async close(): Promise<void> {
    if (this.closed) {
      return;
    }
    this.closed = true;
    await this.stopActiveProcesses();

    if (this.state.workspaceRootOwned) {
      await rm(this.state.workspaceRootPath, { recursive: true, force: true });
    }
  }

  private async stopActiveProcesses(): Promise<void> {
    await Promise.all(
      [...this.activeProcesses.values()].map(async (activeProcess) => {
        await this.terminateActiveProcess(activeProcess);
      }),
    );
    this.activeProcesses.clear();
  }

  private async terminateActiveProcess(
    activeProcess: ActiveProcess,
    graceMs = 250,
  ): Promise<void> {
    if (!activeProcess.done) {
      this.signalActiveProcess(activeProcess, 'SIGTERM');
      await waitForProcessOrTimeout(activeProcess, graceMs);
    }
    if (!activeProcess.done) {
      this.signalActiveProcess(activeProcess, 'SIGKILL');
      await activeProcess.donePromise.catch(() => {});
      if (activeProcess.done) {
        await Promise.race([
          activeProcess.outputClosedPromise,
          new Promise((resolve) => setTimeout(resolve, 250)),
        ]);
      }
    }
  }

  private signalActiveProcess(
    activeProcess: ActiveProcess,
    signal: NodeJS.Signals,
  ): void {
    if (activeProcess.processGroup && activeProcess.child.pid !== undefined) {
      try {
        process.kill(-activeProcess.child.pid, signal);
        return;
      } catch {
        // Fall back to the direct child if process-group delivery is unavailable.
      }
    }
    activeProcess.child.kill(signal);
  }

  resolveSandboxPath(
    path?: string,
    options: ResolveSandboxPathOptions = {},
  ): string {
    const resolved = this.resolveSandboxPathTarget(path, options);
    const workspaceRelativePath = resolved.workspaceRelativePath ?? '';
    if (!workspaceRelativePath && !resolved.grant) {
      return this.state.workspaceRootPath;
    }
    if (resolved.grant) {
      return validateResolvedHostPath({
        path,
        resolvedPath: resolved.path,
        allowedRoot: resolved.grant.path,
      });
    }

    const mountPath = this.resolveLocalBindMountPath(
      workspaceRelativePath,
      path,
      options,
    );
    if (mountPath) {
      return mountPath;
    }

    const resolvedPath = resolve(
      this.state.workspaceRootPath,
      workspaceRelativePath,
    );
    const relativeToRoot = relative(this.state.workspaceRootPath, resolvedPath);
    if (relativeHostPathEscapesRoot(relativeToRoot)) {
      throw new UserError(`Sandbox path "${path}" escapes the workspace root.`);
    }
    return validateResolvedHostPath({
      path,
      resolvedPath,
      allowedRoot: this.state.workspaceRootPath,
    });
  }

  protected resolveCommandWorkdir(path?: string): string {
    return this.resolveSandboxPath(path);
  }

  private grepDisplayPathFormatter(
    args: GrepFilesArgs,
    searchPath: string,
    searchPathIsFile: boolean,
  ): (path: string) => string {
    const workspaceRoot = this.state.workspaceRootPath;
    const resolvedSearchPath = resolve(searchPath);
    const logicalBase = this.resolveLogicalPath(args.path);
    return (path: string): string => {
      const resolvedPath = resolve(path);
      const workspaceRelativePath = relative(workspaceRoot, resolvedPath);
      if (!relativeHostPathEscapesRoot(workspaceRelativePath)) {
        return toPosixPath(workspaceRelativePath);
      }

      if (searchPathIsFile && resolvedPath === resolvedSearchPath) {
        return logicalBase || args.path || '.';
      }

      const searchRelativePath = relative(resolvedSearchPath, resolvedPath);
      if (!relativeHostPathEscapesRoot(searchRelativePath)) {
        return joinLogicalGrepPath(logicalBase, searchRelativePath);
      }

      return path;
    };
  }

  private async sortGrepFileMatches(paths: string[]): Promise<string[]> {
    const stats = await Promise.allSettled(
      paths.map(async (path) => await stat(path)),
    );
    return paths
      .map((path, index) => {
        const result = stats[index];
        return [
          path,
          result?.status === 'fulfilled' ? (result.value.mtimeMs ?? 0) : 0,
        ] as const;
      })
      .sort((a, b) => {
        if (process.env.NODE_ENV === 'test') {
          return a[0].localeCompare(b[0]);
        }
        const mtimeComparison = b[1] - a[1];
        return mtimeComparison === 0
          ? a[0].localeCompare(b[0])
          : mtimeComparison;
      })
      .map(([path]) => path);
  }

  private globDisplayPathFormatter(
    args: GlobFilesArgs,
    searchPath: string,
  ): (path: string) => string {
    const workspaceRoot = this.state.workspaceRootPath;
    const resolvedSearchPath = resolve(searchPath);
    const logicalBase = this.safeLogicalPath(args.path);
    return (path: string): string => {
      const resolvedPath = resolve(path);
      const workspaceRelativePath = relative(workspaceRoot, resolvedPath);
      if (!relativeHostPathEscapesRoot(workspaceRelativePath)) {
        return toPosixPath(workspaceRelativePath);
      }

      const searchRelativePath = relative(resolvedSearchPath, resolvedPath);
      if (!relativeHostPathEscapesRoot(searchRelativePath)) {
        return joinLogicalGrepPath(logicalBase, searchRelativePath);
      }

      return path;
    };
  }

  private safeLogicalPath(path?: string): string {
    try {
      return this.resolveLogicalPath(path);
    } catch {
      return '';
    }
  }

  private resolveSandboxPathTarget(
    path?: string,
    options: ResolveSandboxPathOptions = {},
  ): ResolvedSandboxPath {
    return new WorkspacePathPolicy({
      root: this.state.manifest.root,
      extraPathGrants: this.state.manifest.extraPathGrants,
    }).resolve(path, options);
  }

  private resolveLocalBindMountPath(
    workspaceRelativePath: string,
    path: string | undefined,
    options: ResolveSandboxPathOptions,
  ): string | undefined {
    return this.resolveLocalBindMountTarget(
      workspaceRelativePath,
      path,
      options,
    )?.resolvedPath;
  }

  private resolveLocalBindMountTarget(
    workspaceRelativePath: string,
    path: string | undefined,
    options: ResolveSandboxPathOptions,
  ): { source: string; childPath: string; resolvedPath: string } | undefined {
    for (const { entry, mountPath } of this.state.manifest.mountTargets()) {
      const source = localBindMountSource(entry);
      if (!source) {
        continue;
      }
      const mountRelativePath = new WorkspacePathPolicy({
        root: this.state.manifest.root,
      }).resolve(mountPath).workspaceRelativePath;
      if (typeof mountRelativePath !== 'string') {
        continue;
      }
      if (!pathWithinLogicalRoot(workspaceRelativePath, mountRelativePath)) {
        continue;
      }
      if (options.forWrite && entry.readOnly !== false) {
        throw new UserError(
          `Sandbox path "${path}" is inside a read-only local bind mount.`,
        );
      }
      const childPath =
        workspaceRelativePath === mountRelativePath
          ? ''
          : workspaceRelativePath.slice(mountRelativePath.length + 1);
      const resolvedPath = childPath ? resolve(source, childPath) : source;
      return {
        source,
        childPath,
        resolvedPath: validateResolvedHostPath({
          path,
          resolvedPath,
          allowedRoot: source,
        }),
      };
    }
    return undefined;
  }

  protected resolveLogicalPath(path?: string): string {
    if (!path || path.trim().length === 0) {
      return '';
    }

    const logicalRoot = this.state.manifest.root;
    const trimmed = path.trim();
    if (trimmed === logicalRoot) {
      return '';
    }

    if (logicalRoot === '/') {
      return normalizeRelativePath(
        trimmed.startsWith('/') ? trimmed.slice(1) : trimmed,
      );
    }

    if (trimmed.startsWith(`${logicalRoot}/`)) {
      return normalizeRelativePath(trimmed.slice(logicalRoot.length + 1));
    }

    if (trimmed.startsWith('/')) {
      throw new UserError(
        `Sandbox path "${path}" must stay within ${logicalRoot}.`,
      );
    }

    return normalizeRelativePath(trimmed);
  }

  protected async spawnShellCommand(
    command: string,
    args: {
      cwd: string;
      logicalCwd: string;
      shell?: string;
      login: boolean;
      runAs?: string;
      tty?: boolean;
      processGroup?: boolean;
    },
  ): Promise<ChildProcessWithoutNullStreams> {
    const { shellPath, flag } = resolveLocalShellCommand({
      shell: args.shell,
      defaultShell: this.defaultShell,
      envShell: process.env.SHELL,
      login: args.login,
    });
    const runAs = await this.resolveCommandRunAs(args.runAs);
    const env = buildCommandEnvironment({
      environment: this.state.environment,
      shellPath,
      home: this.state.workspaceRootPath,
      pwd: args.logicalCwd,
    });
    if (args.tty) {
      return spawnInPseudoTerminal(shellPath, [flag, command], {
        cwd: args.cwd,
        env,
        ...(runAs
          ? {
              uid: runAs.uid,
              gid: runAs.gid,
            }
          : {}),
      });
    }

    return spawn(shellPath, [flag, command], {
      cwd: args.cwd,
      env,
      detached: args.processGroup ?? false,
      ...(runAs
        ? {
            uid: runAs.uid,
            gid: runAs.gid,
          }
        : {}),
      stdio: 'pipe',
    });
  }

  /**
   * 直接 spawn 二进制 + argv,**不启动 shell**(spawn 默认 shell:false)。因此没有
   * rc/profile/BASH_ENV 启动钩子、没有分词/展开/元字符——argv 原样到达 execvp。
   * PATH 来自 buildCommandEnvironment 的 DEFAULT_SANDBOX_COMMAND_PATH。
   */
  protected async spawnDirectCommand(
    command: string,
    commandArgs: string[],
    args: {
      cwd: string;
      logicalCwd: string;
      runAs?: string;
      processGroup?: boolean;
    },
  ): Promise<ChildProcessWithoutNullStreams> {
    const runAs = await this.resolveCommandRunAs(args.runAs);
    const env = buildCommandEnvironment({
      environment: this.state.environment,
      shellPath: this.defaultShell ?? process.env.SHELL ?? '/bin/sh',
      home: this.state.workspaceRootPath,
      pwd: args.logicalCwd,
    });
    return spawn(command, commandArgs, {
      cwd: args.cwd,
      env,
      detached: args.processGroup ?? false,
      ...(runAs
        ? {
            uid: runAs.uid,
            gid: runAs.gid,
          }
        : {}),
      stdio: 'pipe',
    });
  }

  protected translateCommandInput(command: string): string {
    // Unix-local is not a chroot; command rewriting preserves manifest-root UX while
    // filesystem APIs continue to enforce host-path containment separately.
    if (this.state.manifest.root === '/') {
      return translateRootManifestCommandInput(
        command,
        this.state.workspaceRootPath,
      );
    }
    return translateManifestRootCommandInput(
      command,
      this.state.manifest.root,
      this.state.workspaceRootPath,
    );
  }

  protected translateCommandOutput(output: string): string {
    if (this.state.manifest.root === '/') {
      return translateRootManifestCommandOutput(
        output,
        this.state.workspaceRootPath,
      );
    }
    return translateManifestRootCommandOutput(
      output,
      this.state.manifest.root,
      this.state.workspaceRootPath,
    );
  }

  protected commandOutputTranslationCarryChars(): number {
    return Math.max(
      0,
      ...workspaceRootOutputPaths(this.state.workspaceRootPath).map(
        (path) => path.length - 1,
      ),
    );
  }

  protected async materializeRestoredWorkspaceMounts(): Promise<void> {
    await materializeLocalWorkspaceManifestMounts(
      this.state.manifest,
      this.state.workspaceRootPath,
    );
  }

  protected logicalWorkdirForPath(path?: string): string {
    return this.resolveSandboxPathTarget(path).path;
  }

  protected async resolveCommandRunAs(
    runAs?: string,
  ): Promise<UnixRunAsIdentity | undefined> {
    const identity = await this.resolveRunAsIdentity(runAs);
    if (!identity || identity.isCurrentUser) {
      return identity;
    }
    if (typeof process.getuid !== 'function' || process.getuid() !== 0) {
      throw new UserError(
        `Unix-local sandbox runAs="${runAs}" requires the host process to run as root or the requested user.`,
      );
    }
    return identity;
  }

  async resolveFilesystemRunAs(
    runAs?: string,
  ): Promise<UnixRunAsIdentity | undefined> {
    const identity = await this.resolveRunAsIdentity(runAs);
    if (!identity || identity.isCurrentUser) {
      return identity;
    }
    if (typeof process.getuid !== 'function' || process.getuid() !== 0) {
      throw new UserError(
        `Unix-local sandbox filesystem operations cannot honor runAs="${runAs}" without root privileges.`,
      );
    }
    return identity;
  }

  protected async resolveRunAsIdentity(
    runAs?: string,
  ): Promise<UnixRunAsIdentity | undefined> {
    if (!runAs || runAs.trim().length === 0) {
      return undefined;
    }

    const requestedUser = runAs.trim();
    const currentUser = userInfo().username;
    const currentUid =
      typeof process.getuid === 'function' ? process.getuid() : undefined;
    const currentGid =
      typeof process.getgid === 'function' ? process.getgid() : undefined;

    if (
      requestedUser === currentUser &&
      currentUid !== undefined &&
      currentGid !== undefined
    ) {
      return {
        username: currentUser,
        uid: currentUid,
        gid: currentGid,
        isCurrentUser: true,
      };
    }

    const [uidResult, gidResult] = await Promise.all([
      runSandboxProcess('id', ['-u', requestedUser], {
        timeoutMs: RUN_AS_LOOKUP_TIMEOUT_MS,
      }),
      runSandboxProcess('id', ['-g', requestedUser], {
        timeoutMs: RUN_AS_LOOKUP_TIMEOUT_MS,
      }),
    ]);
    if (uidResult.status !== 0 || gidResult.status !== 0) {
      throw new UserError(
        `Sandbox runAs user "${requestedUser}" could not be resolved on this host.`,
      );
    }

    const uid = Number(uidResult.stdout.trim());
    const gid = Number(gidResult.stdout.trim());
    return {
      username: requestedUser,
      uid,
      gid,
      isCurrentUser:
        currentUid !== undefined &&
        currentGid !== undefined &&
        uid === currentUid &&
        gid === currentGid,
    };
  }

  private trackChildProcess(
    child: ChildProcessWithoutNullStreams,
    options: {
      tty?: boolean;
      processGroup?: boolean;
      outputCapture?: ForegroundOutputCapture;
    } = {},
  ): ActiveProcess {
    let resolveDone = () => {};
    const donePromise = new Promise<void>((resolve) => {
      resolveDone = resolve;
    });
    let resolveOutputClosed = () => {};
    const outputClosedPromise = new Promise<void>((resolve) => {
      resolveOutputClosed = resolve;
    });

    const activeProcess: ActiveProcess = {
      child,
      tty: options.tty ?? false,
      processGroup: options.processGroup ?? false,
      outputCapture: options.outputCapture,
      output: '',
      stdout: '',
      stderr: '',
      droppedOutputChars: 0,
      droppedStdoutChars: 0,
      droppedStderrChars: 0,
      exitCode: null,
      done: false,
      donePromise,
      resolveDone,
      outputClosedPromise,
      resolveOutputClosed,
    };
    let openOutputStreams = 2;
    const markOutputStreamClosed = () => {
      openOutputStreams -= 1;
      if (openOutputStreams === 0) {
        activeProcess.resolveOutputClosed();
      }
    };

    child.stdout.setEncoding('utf8');
    child.stderr.setEncoding('utf8');
    child.stdout.on('data', (chunk: string) => {
      appendActiveProcessOutput(activeProcess, chunk, 'stdout');
    });
    child.stderr.on('data', (chunk: string) => {
      appendActiveProcessOutput(activeProcess, chunk, 'stderr');
    });
    child.stdout.once('close', markOutputStreamClosed);
    child.stderr.once('close', markOutputStreamClosed);
    child.on('close', (code, signal) => {
      activeProcess.exitCode = code ?? signalToExitCode(signal);
      activeProcess.done = true;
      activeProcess.resolveDone();
    });
    child.on('error', (error) => {
      appendActiveProcessOutput(activeProcess, `${error.message}\n`, 'stderr');
      activeProcess.exitCode = 1;
      activeProcess.done = true;
      activeProcess.resolveOutputClosed();
      activeProcess.resolveDone();
    });

    return activeProcess;
  }

  private async createForegroundOutputCapture(args: {
    outputPreviewBytes?: number;
    outputId?: string;
  }): Promise<ForegroundOutputCapture> {
    const previewBytes = normalizeForegroundOutputPreviewBytes(
      args.outputPreviewBytes,
    );
    const id = safeToolResultId(args.outputId);
    const outputFileName = `${id}.txt`;
    const metadataFileName = `${id}.json`;
    const outputDir = resolve(this.state.workspaceRootPath, TOOL_RESULTS_DIR);
    await mkdir(outputDir, { recursive: true });
    const outputFilePath = resolve(outputDir, outputFileName);
    const outputMetadataPath = resolve(outputDir, metadataFileName);
    return {
      id,
      outputFilePath,
      outputMetadataPath,
      logicalOutputFilePath: joinSandboxLogicalPath(
        this.state.manifest.root,
        `${TOOL_RESULTS_DIR}/${outputFileName}`,
      ),
      logicalOutputMetadataPath: joinSandboxLogicalPath(
        this.state.manifest.root,
        `${TOOL_RESULTS_DIR}/${metadataFileName}`,
      ),
      previewBytes,
      fd: openSync(outputFilePath, 'w'),
      translator: new StreamingOutputTranslator(
        (output) => this.translateCommandOutput(output),
        this.commandOutputTranslationCarryChars(),
      ),
      preview: '',
      previewByteLength: 0,
      bytesSeen: 0,
      bytesWritten: 0,
      fileTruncated: false,
      closed: false,
    };
  }

  private allocateProcessId(activeProcess: ActiveProcess): number {
    const sessionId = this.nextSessionId++;
    this.activeProcesses.set(sessionId, activeProcess);
    return sessionId;
  }
}

type FinalizedForegroundOutput =
  | {
      persisted: false;
    }
  | {
      persisted: true;
      outputFilePath: string;
      outputMetadataPath?: string;
      logicalOutputFilePath: string;
      logicalOutputMetadataPath?: string;
      preview: string;
      previewBytes: number;
      bytesSeen: number;
      bytesWritten: number;
      fileTruncated: boolean;
      writeError?: string;
    };

class StreamingOutputTranslator {
  private buffer = '';
  private readonly carryChars: number;

  constructor(
    private readonly translate: (output: string) => string,
    carryChars: number,
  ) {
    this.carryChars = Math.max(0, carryChars);
  }

  push(chunk: string): string {
    if (this.carryChars === 0) {
      return this.translate(chunk);
    }
    this.buffer += chunk;
    if (this.buffer.length <= this.carryChars) {
      return '';
    }
    const emitLength = this.buffer.length - this.carryChars;
    const emit = this.buffer.slice(0, emitLength);
    this.buffer = this.buffer.slice(emitLength);
    return this.translate(emit);
  }

  flush(): string {
    if (this.buffer.length === 0) {
      return '';
    }
    const remaining = this.buffer;
    this.buffer = '';
    return this.translate(remaining);
  }
}

function appendForegroundOutputCapture(
  capture: ForegroundOutputCapture | undefined,
  rawChunk: string,
): void {
  if (!capture || capture.closed) {
    return;
  }
  const chunk = capture.translator.push(rawChunk);
  appendTranslatedForegroundOutput(capture, chunk);
}

function appendTranslatedForegroundOutput(
  capture: ForegroundOutputCapture,
  chunk: string,
): void {
  if (chunk.length === 0) {
    return;
  }

  const chunkBytes = byteLength(chunk);
  capture.bytesSeen += chunkBytes;
  if (capture.previewByteLength < capture.previewBytes) {
    const previewChunk = truncateStringToUtf8Bytes(
      chunk,
      capture.previewBytes - capture.previewByteLength,
    );
    capture.preview += previewChunk;
    capture.previewByteLength += byteLength(previewChunk);
  }

  if (
    capture.writeError ||
    capture.bytesWritten >= MAX_FOREGROUND_OUTPUT_FILE_BYTES
  ) {
    capture.fileTruncated = true;
    return;
  }

  const remainingBytes =
    MAX_FOREGROUND_OUTPUT_FILE_BYTES - capture.bytesWritten;
  const chunkToWrite =
    chunkBytes <= remainingBytes
      ? chunk
      : truncateStringToUtf8Bytes(chunk, remainingBytes);
  const bytesToWrite = byteLength(chunkToWrite);
  if (bytesToWrite === 0) {
    capture.fileTruncated = true;
    return;
  }

  try {
    writeSync(capture.fd, chunkToWrite, undefined, 'utf8');
    capture.bytesWritten += bytesToWrite;
    if (bytesToWrite < chunkBytes) {
      capture.fileTruncated = true;
    }
  } catch (error) {
    capture.writeError = error instanceof Error ? error.message : String(error);
    capture.fileTruncated = true;
  }
}

async function finalizeForegroundOutputCapture(
  capture: ForegroundOutputCapture | undefined,
  metadata: {
    command: string;
    exitCode: number;
    wallTimeSeconds: number;
  },
): Promise<FinalizedForegroundOutput> {
  if (!capture) {
    return { persisted: false };
  }

  appendTranslatedForegroundOutput(capture, capture.translator.flush());
  closeForegroundOutputCapture(capture);

  const shouldPersist =
    capture.bytesSeen > capture.previewBytes ||
    capture.fileTruncated ||
    Boolean(capture.writeError);
  if (!shouldPersist) {
    await unlink(capture.outputFilePath).catch(() => {});
    return { persisted: false };
  }

  let logicalOutputMetadataPath: string | undefined =
    capture.logicalOutputMetadataPath;
  try {
    await writeFile(
      capture.outputMetadataPath,
      JSON.stringify(
        {
          tool: 'Bash',
          id: capture.id,
          command: metadata.command,
          exitCode: metadata.exitCode,
          wallTimeSeconds: metadata.wallTimeSeconds,
          outputFilePath: capture.logicalOutputFilePath,
          outputMetadataPath: capture.logicalOutputMetadataPath,
          outputFileSizeBytes: capture.bytesWritten,
          outputBytesSeen: capture.bytesSeen,
          outputPreviewBytes: capture.previewBytes,
          outputTruncated: capture.fileTruncated,
          writeError: capture.writeError,
          maxOutputFileBytes: MAX_FOREGROUND_OUTPUT_FILE_BYTES,
        },
        null,
        2,
      ),
      'utf8',
    );
  } catch {
    logicalOutputMetadataPath = undefined;
  }

  return {
    persisted: true,
    outputFilePath: capture.outputFilePath,
    outputMetadataPath: logicalOutputMetadataPath
      ? capture.outputMetadataPath
      : undefined,
    logicalOutputFilePath: capture.logicalOutputFilePath,
    logicalOutputMetadataPath,
    preview: capture.preview,
    previewBytes: capture.previewBytes,
    bytesSeen: capture.bytesSeen,
    bytesWritten: capture.bytesWritten,
    fileTruncated: capture.fileTruncated,
    writeError: capture.writeError,
  };
}

function closeForegroundOutputCapture(capture: ForegroundOutputCapture): void {
  if (capture.closed) {
    return;
  }
  capture.closed = true;
  try {
    closeSync(capture.fd);
  } catch {
    // Ignore close failures; the model-facing result will still include the preview.
  }
}

function buildPersistedOutputMessage(
  output: Extract<FinalizedForegroundOutput, { persisted: true }>,
): string {
  const saved = output.fileTruncated
    ? 'Partial output saved to'
    : 'Full output saved to';
  const lines = [
    '<persisted-output>',
    `Output too large (${formatByteSize(output.bytesSeen)}). ${saved}: ${output.logicalOutputFilePath}`,
  ];
  if (output.logicalOutputMetadataPath) {
    lines.push(`Metadata saved to: ${output.logicalOutputMetadataPath}`);
  }
  if (output.fileTruncated) {
    lines.push(
      `Output file was truncated at ${formatByteSize(output.bytesWritten)}.`,
    );
  }
  if (output.writeError) {
    lines.push(`Output file write error: ${output.writeError}`);
  }
  lines.push('');
  lines.push(`Preview (first ${formatByteSize(output.previewBytes)}):`);
  lines.push(output.preview);
  if (output.bytesSeen > output.previewBytes) {
    lines.push('...');
  }
  lines.push('</persisted-output>');
  return lines.join('\n');
}

function normalizeForegroundOutputPreviewBytes(value?: number): number {
  if (value == null || !Number.isFinite(value) || value <= 0) {
    return DEFAULT_EXEC_FOREGROUND_OUTPUT_PREVIEW_BYTES;
  }
  return Math.min(Math.floor(value), MAX_EXEC_FOREGROUND_OUTPUT_PREVIEW_BYTES);
}

function safeToolResultId(value?: string): string {
  const raw = value && value.trim().length > 0 ? value : randomUUID();
  const safe = raw.replace(/[^A-Za-z0-9_.-]/g, '_').slice(0, 96);
  return safe.length > 0 ? safe : randomUUID();
}

function truncateStringToUtf8Bytes(value: string, maxBytes: number): string {
  if (maxBytes <= 0) {
    return '';
  }
  const bytes = new TextEncoder().encode(value);
  if (bytes.byteLength <= maxBytes) {
    return value;
  }
  return new TextDecoder().decode(bytes.slice(0, maxBytes));
}

function byteLength(value: string): number {
  return new TextEncoder().encode(value).byteLength;
}

function approximateTokenCountFromBytes(bytes: number): number {
  if (bytes <= 0) {
    return 0;
  }
  return Math.ceil(bytes / 4);
}

function formatByteSize(bytes: number): string {
  if (bytes < 1024) {
    return `${bytes} B`;
  }
  if (bytes < 1024 * 1024) {
    return `${(bytes / 1024).toFixed(1)} KB`;
  }
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

async function waitForProcessOrTimeout(
  activeProcess: ActiveProcess,
  timeoutMs: number,
): Promise<void> {
  if (activeProcess.done) {
    return;
  }

  await Promise.race([
    activeProcess.donePromise,
    new Promise((resolve) => setTimeout(resolve, timeoutMs)),
  ]);
  if (activeProcess.done) {
    await activeProcess.outputClosedPromise;
  }
}

type ConsumedActiveProcessOutput = {
  output: string;
  stdout: string;
  stderr: string;
};

function consumeActiveProcessOutput(
  activeProcess: ActiveProcess,
): ConsumedActiveProcessOutput {
  const output = withDroppedOutputPrefix(
    activeProcess.output,
    activeProcess.droppedOutputChars,
  );
  const stdout = withDroppedOutputPrefix(
    activeProcess.stdout,
    activeProcess.droppedStdoutChars,
  );
  const stderr = withDroppedOutputPrefix(
    activeProcess.stderr,
    activeProcess.droppedStderrChars,
  );
  activeProcess.output = '';
  activeProcess.stdout = '';
  activeProcess.stderr = '';
  activeProcess.droppedOutputChars = 0;
  activeProcess.droppedStdoutChars = 0;
  activeProcess.droppedStderrChars = 0;

  return { output, stdout, stderr };
}

function withDroppedOutputPrefix(output: string, droppedChars: number): string {
  if (droppedChars === 0) {
    return output;
  }

  return `[...${droppedChars} characters truncated from process output...]\n${output}`;
}

function validateResolvedHostPath(args: {
  path?: string;
  resolvedPath: string;
  allowedRoot: string;
}): string {
  const allowedRootRealPath = realpathForValidation(
    args.allowedRoot,
    args.path,
  );
  const existingPath = nearestExistingPath(args.resolvedPath);
  if (!existingPath) {
    throw new UserError(
      `Sandbox path "${args.path}" escapes the workspace root.`,
    );
  }
  const realPath = realpathForValidation(existingPath, args.path);
  if (!isHostPathWithinRoot(allowedRootRealPath, realPath)) {
    throw new UserError(
      `Sandbox path "${args.path}" escapes the workspace root.`,
    );
  }
  return args.resolvedPath;
}

function realpathForValidation(path: string, originalPath?: string): string {
  try {
    return realpathSync(path);
  } catch {
    throw new UserError(
      `Sandbox path "${originalPath}" escapes the workspace root.`,
    );
  }
}

function localBindMountSource(entry: {
  mountStrategy?: { type: string };
  source?: unknown;
  type: string;
}): string | undefined {
  if (entry.type !== 'mount' || typeof entry.source !== 'string') {
    return undefined;
  }
  if (
    entry.mountStrategy !== undefined &&
    entry.mountStrategy.type !== 'local_bind'
  ) {
    return undefined;
  }
  return isAbsolute(entry.source) ? entry.source : undefined;
}

function pathWithinLogicalRoot(path: string, root: string): boolean {
  return path === root || path.startsWith(`${root}/`);
}

function buildCommandEnvironment(args: {
  environment: Record<string, string>;
  shellPath: string;
  home: string;
  pwd: string;
}): NodeJS.ProcessEnv {
  return {
    PATH: DEFAULT_SANDBOX_COMMAND_PATH,
    HOME: args.home,
    USER: 'sandbox',
    LOGNAME: 'sandbox',
    SHELL: args.shellPath,
    TMPDIR: '/tmp',
    ...args.environment,
    PWD: args.pwd,
  };
}

function translateRootManifestCommandInput(
  command: string,
  workspaceRootPath: string,
): string {
  return command.replace(
    /(^|[\s"'=<>])\/([^\s"'|&;<>(){}]*)/g,
    (_match, prefix: string, pathSuffix: string) =>
      `${prefix}${workspaceRootPath}/${pathSuffix}`,
  );
}

function translateManifestRootCommandInput(
  command: string,
  manifestRoot: string,
  workspaceRootPath: string,
): string {
  const escapedManifestRoot = escapeRegExp(manifestRoot);
  const pathPrefix = String.raw`(^|[\s"'=<>])`;
  const pathSuffix = String.raw`(?=$|[\/\s"'|&;<>(){}])`;
  return command.replace(
    new RegExp(`${pathPrefix}${escapedManifestRoot}${pathSuffix}`, 'g'),
    (_match, prefix: string) => `${prefix}${workspaceRootPath}`,
  );
}

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function translateRootManifestCommandOutput(
  output: string,
  workspaceRootPath: string,
): string {
  return translateWorkspaceRootCommandOutput(output, '/', workspaceRootPath);
}

function translateWorkspaceRootCommandOutput(
  output: string,
  manifestRoot: string,
  workspaceRootPath: string,
): string {
  const nestedPathReplacement = manifestRoot === '/' ? '/' : `${manifestRoot}/`;
  let translated = output;
  for (const path of workspaceRootOutputPaths(workspaceRootPath)) {
    translated = translated
      .split(`${path}/`)
      .join(nestedPathReplacement)
      .split(path)
      .join(manifestRoot);
  }
  return translated;
}

function workspaceRootOutputPaths(workspaceRootPath: string): string[] {
  const paths = [workspaceRootPath];
  try {
    paths.push(realpathSync(workspaceRootPath));
  } catch {
    return paths;
  }
  return Array.from(new Set(paths)).sort((a, b) => b.length - a.length);
}

function translateManifestRootCommandOutput(
  output: string,
  manifestRoot: string,
  workspaceRootPath: string,
): string {
  return translateWorkspaceRootCommandOutput(
    output,
    manifestRoot,
    workspaceRootPath,
  );
}

function appendActiveProcessOutput(
  activeProcess: ActiveProcess,
  chunk: string,
  stream: ActiveProcessOutputStream,
): void {
  if (chunk.length === 0) {
    return;
  }

  appendForegroundOutputCapture(activeProcess.outputCapture, chunk);

  const combined = appendBoundedOutput(
    activeProcess.output,
    activeProcess.droppedOutputChars,
    chunk,
  );
  activeProcess.output = combined.output;
  activeProcess.droppedOutputChars = combined.droppedChars;

  if (stream === 'stdout') {
    const stdout = appendBoundedOutput(
      activeProcess.stdout,
      activeProcess.droppedStdoutChars,
      chunk,
    );
    activeProcess.stdout = stdout.output;
    activeProcess.droppedStdoutChars = stdout.droppedChars;
    return;
  }

  const stderr = appendBoundedOutput(
    activeProcess.stderr,
    activeProcess.droppedStderrChars,
    chunk,
  );
  activeProcess.stderr = stderr.output;
  activeProcess.droppedStderrChars = stderr.droppedChars;
}

function signalToExitCode(signal: NodeJS.Signals | null): number {
  if (signal === 'SIGINT') {
    return 130;
  }
  return 1;
}

function nearestExistingPath(path: string): string | undefined {
  let current = path;
  while (true) {
    try {
      lstatSync(current);
      return current;
    } catch (error) {
      if ((error as NodeJS.ErrnoException).code !== 'ENOENT') {
        throw error;
      }
    }

    const parent = dirname(current);
    if (parent === current) {
      return undefined;
    }
    current = parent;
  }
}

function appendBoundedOutput(
  output: string,
  droppedChars: number,
  chunk: string,
): { output: string; droppedChars: number } {
  const nextLength = output.length + chunk.length;
  if (nextLength <= MAX_ACTIVE_PROCESS_OUTPUT_CHARS) {
    return {
      output: output + chunk,
      droppedChars,
    };
  }

  const nextDroppedChars =
    droppedChars + nextLength - MAX_ACTIVE_PROCESS_OUTPUT_CHARS;
  if (chunk.length >= MAX_ACTIVE_PROCESS_OUTPUT_CHARS) {
    return {
      output: chunk.slice(-MAX_ACTIVE_PROCESS_OUTPUT_CHARS),
      droppedChars: nextDroppedChars,
    };
  }

  const existingCharsToKeep = MAX_ACTIVE_PROCESS_OUTPUT_CHARS - chunk.length;
  return {
    output: output.slice(-existingCharsToKeep) + chunk,
    droppedChars: nextDroppedChars,
  };
}

export class UnixLocalSandboxClient
  implements
    SandboxClient<UnixLocalSandboxClientOptions, UnixLocalSandboxSessionState>
{
  readonly backendId = 'unix_local';
  readonly supportsDefaultOptions = true;
  private readonly options: UnixLocalSandboxClientOptions;

  constructor(options: UnixLocalSandboxClientOptions = {}) {
    this.options = options;
  }

  async create(
    args?: SandboxClientCreateArgs<UnixLocalSandboxClientOptions> | Manifest,
    manifestOptions?: UnixLocalSandboxClientOptions,
  ): Promise<UnixLocalSandboxSession> {
    const createArgs = normalizeSandboxClientCreateArgs(args, manifestOptions);
    const manifest = createArgs.manifest;
    const resolvedOptions = {
      ...this.options,
      ...createArgs.options,
      ...(createArgs.snapshot
        ? { snapshot: createArgs.snapshot as LocalSandboxSnapshotSpec }
        : {}),
      ...(createArgs.concurrencyLimits
        ? { concurrencyLimits: createArgs.concurrencyLimits }
        : {}),
    };
    assertLocalWorkspaceManifestMetadataSupported(
      'UnixLocalSandboxClient',
      manifest,
    );
    const workspaceRootPath = await mkdtemp(
      join(resolvedOptions.workspaceBaseDir ?? tmpdir(), 'sandbox-'),
    );

    await materializeLocalWorkspaceManifest(manifest, workspaceRootPath, {
      concurrencyLimits: resolvedOptions.concurrencyLimits,
    });
    const environment = await manifest.resolveEnvironment();
    const configuredExposedPorts = normalizeExposedPorts(
      resolvedOptions.exposedPorts,
    );

    return new UnixLocalSandboxSession({
      state: {
        manifest,
        workspaceRootPath,
        workspaceRootOwned: true,
        environment,
        snapshotSpec: resolvedOptions.snapshot ?? null,
        snapshot: null,
        configuredExposedPorts,
      },
      defaultShell: resolvedOptions.defaultShell,
    });
  }

  async resume(
    state: UnixLocalSandboxSessionState,
  ): Promise<UnixLocalSandboxSession> {
    const restoredState = await this.restoreIfNeeded(state);
    return new UnixLocalSandboxSession({
      state: restoredState,
      defaultShell: this.options.defaultShell,
    });
  }

  async serializeSessionState(
    state: UnixLocalSandboxSessionState,
  ): Promise<Record<string, unknown>> {
    const snapshotSpec = state.snapshotSpec ?? this.options.snapshot ?? null;
    const snapshot = await persistLocalSnapshot(
      'UnixLocalSandboxClient',
      state,
      snapshotSpec,
    );
    state.snapshotSpec = snapshotSpec;

    return {
      manifest: serializeManifest(state.manifest),
      workspaceRootPath: state.workspaceRootPath,
      workspaceRootOwned: state.workspaceRootOwned,
      environment: sanitizeEnvironmentForPersistence(state),
      snapshotSpec: serializeLocalSnapshotSpec(snapshotSpec),
      snapshot,
      snapshotFingerprint: state.snapshotFingerprint ?? null,
      snapshotFingerprintVersion: state.snapshotFingerprintVersion ?? null,
      configuredExposedPorts: state.configuredExposedPorts ?? [],
      exposedPorts: state.exposedPorts ?? null,
    };
  }

  async deserializeSessionState(
    state: Record<string, unknown>,
  ): Promise<UnixLocalSandboxSessionState> {
    return deserializeLocalSandboxSessionStateValues(
      state,
      this.options.snapshot,
    );
  }

  private async restoreIfNeeded(
    state: UnixLocalSandboxSessionState,
  ): Promise<UnixLocalSandboxSessionState> {
    if (await pathExists(state.workspaceRootPath)) {
      if (await canReuseLocalSnapshotWorkspace(state)) {
        return state;
      }
      if (await localSnapshotIsRestorable(state)) {
        return await restoreSnapshotAndMounts(state, state.workspaceRootPath);
      }
      return state;
    }

    if (!(await localSnapshotIsRestorable(state))) {
      throw new UserError(
        'UnixLocal sandbox workspace is unavailable and no local snapshot could be restored.',
      );
    }

    const workspaceRootPath = await mkdtemp(
      join(this.options.workspaceBaseDir ?? tmpdir(), 'sandbox-'),
    );

    return await restoreSnapshotAndMounts(
      {
        ...state,
        workspaceRootPath,
        workspaceRootOwned: true,
      },
      workspaceRootPath,
    );
  }
}

function countOccurrences(source: string, needle: string): number {
  let count = 0;
  let index = 0;
  while (true) {
    index = source.indexOf(needle, index);
    if (index === -1) {
      return count;
    }
    count += 1;
    index += needle.length;
  }
}

function joinLogicalGrepPath(base: string, child: string): string {
  const normalizedChild = toPosixPath(child);
  if (!base) {
    return normalizedChild;
  }
  if (!normalizedChild) {
    return base;
  }
  return pathPosix.join(base, normalizedChild);
}

function toPosixPath(path: string): string {
  return path.split(/[\\/]+/u).join('/');
}

async function readLocalTextFileRange(
  filePath: string,
  args: ReadTextFileArgs,
): Promise<ReadTextFileResult> {
  const info = await stat(filePath).catch((error) => {
    if ((error as NodeJS.ErrnoException).code === 'ENOENT') {
      throw new UserError(`文件不存在：${args.path}`);
    }
    throw error;
  });
  if (!info.isFile()) {
    throw new UserError(`路径不是文件：${args.path}`);
  }
  if (
    args.limit === undefined &&
    typeof args.maxBytes === 'number' &&
    info.size > args.maxBytes
  ) {
    throw new UserError(
      `文件内容 (${info.size} 字节) 超过允许的最大大小 (${args.maxBytes} 字节)。请使用 offset 和 limit 参数读取文件的特定范围，或先搜索特定内容，而不是一次读取完整文件。`,
    );
  }

  if (info.size < 10 * 1024 * 1024) {
    const bytes = await readFile(filePath);
    const content = decodeUtf8Text(bytes, args.path);
    return readTextRangeFromString(content, args, info.size, info.mtimeMs);
  }

  return await readTextRangeFromStream(filePath, args, info.size, info.mtimeMs);
}

function decodeUtf8Text(bytes: Uint8Array, path: string): string {
  if (bytes.includes(0)) {
    throw new UserError(`文件看起来是二进制文件，不能按文本读取：${path}`);
  }
  try {
    return new TextDecoder('utf-8', { fatal: true }).decode(bytes);
  } catch {
    throw new UserError(`文件不是有效的 UTF-8 文本：${path}`);
  }
}

function readTextRangeFromString(
  raw: string,
  args: ReadTextFileArgs,
  totalBytes: number,
  mtimeMs: number,
): ReadTextFileResult {
  const text = normalizeTextForRead(raw);
  const lines = splitReadLines(text);
  const startLine = args.offset ?? 1;
  const selected = selectReadLines(lines, startLine, args.limit);
  const content = selected.join('\n');
  return {
    path: args.path,
    content,
    startLine,
    lineCount: selected.length,
    totalLines: lines.length,
    totalBytes,
    readBytes: Buffer.byteLength(content, 'utf8'),
    mtimeMs,
  };
}

async function readTextRangeFromStream(
  filePath: string,
  args: ReadTextFileArgs,
  totalBytes: number,
  mtimeMs: number,
): Promise<ReadTextFileResult> {
  const startLine = args.offset ?? 1;
  const endLine =
    args.limit === undefined
      ? Number.POSITIVE_INFINITY
      : startLine + args.limit;
  const selected: string[] = [];
  let totalLines = 0;
  let partial = '';
  let isFirstChunk = true;

  await new Promise<void>((resolvePromise, reject) => {
    const stream = createReadStream(filePath, {
      encoding: 'utf8',
      highWaterMark: 512 * 1024,
    });
    stream.on('data', (chunk: string | Buffer) => {
      if (typeof chunk !== 'string') {
        chunk = chunk.toString('utf8');
      }
      if (isFirstChunk) {
        isFirstChunk = false;
        if (chunk.charCodeAt(0) === 0xfeff) {
          chunk = chunk.slice(1);
        }
      }
      const data = partial.length > 0 ? partial + chunk : chunk;
      partial = '';
      let start = 0;
      let newline = data.indexOf('\n', start);
      while (newline !== -1) {
        let line = data.slice(start, newline);
        if (line.endsWith('\r')) {
          line = line.slice(0, -1);
        }
        totalLines += 1;
        if (totalLines >= startLine && totalLines < endLine) {
          selected.push(line);
        }
        start = newline + 1;
        newline = data.indexOf('\n', start);
      }
      if (start < data.length) {
        partial = data.slice(start);
      }
    });
    stream.once('end', () => {
      if (partial.length > 0) {
        let line = partial;
        if (line.endsWith('\r')) {
          line = line.slice(0, -1);
        }
        totalLines += 1;
        if (totalLines >= startLine && totalLines < endLine) {
          selected.push(line);
        }
      }
      resolvePromise();
    });
    stream.once('error', reject);
  });

  const content = selected.join('\n');
  return {
    path: args.path,
    content,
    startLine,
    lineCount: selected.length,
    totalLines,
    totalBytes,
    readBytes: Buffer.byteLength(content, 'utf8'),
    mtimeMs,
  };
}

function normalizeTextForRead(raw: string): string {
  const withoutBom = raw.charCodeAt(0) === 0xfeff ? raw.slice(1) : raw;
  return withoutBom.replace(/\r\n/gu, '\n').replace(/\r/gu, '\n');
}

function splitReadLines(text: string): string[] {
  if (text.length === 0) {
    return [];
  }
  const lines = text.split('\n');
  if (text.endsWith('\n')) {
    lines.pop();
  }
  return lines;
}

function selectReadLines(
  lines: string[],
  startLine: number,
  limit: number | undefined,
): string[] {
  const startIndex = Math.max(0, startLine - 1);
  const endIndex =
    limit === undefined
      ? lines.length
      : Math.min(startIndex + limit, lines.length);
  return lines.slice(startIndex, endIndex);
}

async function restoreSnapshotAndMounts(
  state: UnixLocalSandboxSessionState,
  workspaceRootPath: string,
): Promise<UnixLocalSandboxSessionState> {
  const restoredState = await restoreLocalSnapshotToWorkspace(
    state,
    workspaceRootPath,
  );
  await materializeLocalWorkspaceManifestMounts(
    restoredState.manifest,
    restoredState.workspaceRootPath,
  );
  return restoredState;
}
